/**
 * The Ledger data display on tokens: the table (every `Table.Root` in the app
 * and the `DataTable` wrapper), the alert as the Ledger Banner, the empty
 * state, cards, inline code, Chakra's code block and the stat.
 *
 * The table recipe is what restyles the ~120 existing tables at once: the
 * header on surface-2 at `--text-12` 500 in sentence case, every body row
 * exactly `--row-h` (32px; 28 in the compact `xs` size), a hairline in
 * `--color-border-2` between rows, hover on surface-3 and selection on
 * surface-selected. A cell's height is a minimum in CSS tables, so a cell that
 * still stacks two lines grows its row; those screens are listed in
 * COMPONENT-MAP.md for step 7.
 *
 * Spec: specs/design-system/ledger-data-display.feature
 */
import { defineRecipe, defineSlotRecipe } from "@chakra-ui/react";
import { focusRing } from "./shared";

/**
 * Muted text steps up to fg-2 on a hovered or selected row (fg-3 is never
 * drawn on surface-3). Both the token file's variable and Chakra's semantic
 * tokens that resolve to it are re-pointed inside the row, because a custom
 * property defined on `:root` is substituted there and inherited as a value.
 */
const liftMutedText = {
  "--color-fg-3": "var(--color-fg-2)",
  "--chakra-colors-fg-3": "var(--color-fg-2)",
  // The bridge name Chakra's own recipes read.
  "--chakra-colors-fg-subtle": "var(--color-fg-2)",
} as const;

// `bg`, the key Chakra's own table recipe uses: a `background` key would sit
// beside it rather than replace it, and the stock paint would win.
const rowHover = {
  bg: "var(--color-surface-3)",
  ...liftMutedText,
} as const;

const rowSelected = {
  bg: "var(--color-surface-selected)",
  ...liftMutedText,
} as const;

export const tableSlotRecipe = defineSlotRecipe({
  slots: [
    "root",
    "header",
    "body",
    "row",
    "columnHeader",
    "cell",
    "footer",
    "caption",
  ],
  base: {
    // No radius and no background on the table itself: it is
    // border-collapse, so a rounded or filled root paints square corners over
    // its container's rounded border. Rounding and clipping belong to the
    // container (`DataTable`, `ListTable`, a Card).
    root: {
      background: "transparent",
      color: "var(--color-fg)",
      fontSize: "var(--text-13)",
      lineHeight: "var(--leading-normal)",
      fontVariantNumeric: "tabular-nums",
      borderCollapse: "collapse",
    },
    header: {
      background: "var(--color-surface-2)",
    },
    body: {
      // `data-static` rows (an empty state, skeleton rows) hold no record.
      "& > tr:not([data-static])": { _hover: rowHover },
    },
    row: {
      bg: "transparent",
      _selected: rowSelected,
    },
    columnHeader: {
      background: "var(--color-surface-2)",
      color: "var(--color-fg-3)",
      fontSize: "var(--text-12)",
      fontWeight: "var(--weight-medium)",
      lineHeight: "var(--leading-tight)",
      textTransform: "none",
      letterSpacing: "normal",
      textAlign: "start",
      height: "var(--row-h)",
      paddingBlock: "0",
      verticalAlign: "middle",
      whiteSpace: "nowrap",
      borderBottomWidth: "1px",
      borderBottomStyle: "solid",
      borderBottomColor: "var(--color-border)",
    },
    cell: {
      height: "var(--row-h)",
      paddingBlock: "0",
      verticalAlign: "middle",
      textAlign: "start",
      borderColor: "var(--color-border-2)",
    },
    footer: {
      fontWeight: "var(--weight-medium)",
    },
    caption: {
      fontSize: "var(--text-12)",
      fontWeight: "var(--weight-medium)",
      color: "var(--color-fg-3)",
    },
  },
  variants: {
    variant: {
      // The default: a hairline between body rows, none under the last one
      // (the container's own edge is there).
      line: {
        row: { bg: "transparent" },
        cell: {
          borderBottomWidth: "1px",
          borderBottomStyle: "solid",
          borderBottomColor: "var(--color-border-2)",
        },
        body: {
          "& > tr:last-of-type > td": { borderBottomWidth: "0" },
        },
      },
      // Line plus the table's own outline, for a table that is not in a card.
      outline: {
        root: { boxShadow: "0 0 0 1px var(--color-border)" },
        row: {
          bg: "transparent",
          "&:not(:last-of-type)": {
            borderBottomWidth: "1px",
            borderBottomColor: "var(--color-border-2)",
          },
        },
        footer: {
          borderTopWidth: "1px",
          borderTopColor: "var(--color-border)",
        },
      },
      // Every cell boxed: comparison grids and editable sheets.
      grid: {
        columnHeader: {
          borderWidth: "1px",
          borderStyle: "solid",
          borderColor: "var(--color-border)",
        },
        cell: {
          borderWidth: "1px",
          borderStyle: "solid",
          borderColor: "var(--color-border-2)",
        },
      },
      ghost: {
        row: { bg: "transparent" },
        columnHeader: { borderBottomColor: "var(--color-border-2)" },
      },
    },
    size: {
      // Compact density: 28px rows.
      xs: {
        root: { fontSize: "var(--text-13)" },
        columnHeader: {
          height: "var(--row-h-compact)",
          px: "var(--space-2)",
          py: "0",
        },
        cell: {
          height: "var(--row-h-compact)",
          px: "var(--space-2)",
          py: "0",
        },
      },
      sm: {
        root: { fontSize: "var(--text-13)" },
        columnHeader: { px: "var(--space-2)", py: "0" },
        cell: { px: "var(--space-2)", py: "0" },
      },
      md: {
        root: { fontSize: "var(--text-13)" },
        columnHeader: { px: "var(--space-3)", py: "0" },
        cell: { px: "var(--space-3)", py: "0" },
      },
      lg: {
        root: { fontSize: "var(--text-13)" },
        columnHeader: { px: "var(--space-4)", py: "0" },
        cell: { px: "var(--space-4)", py: "0" },
      },
    },
    interactive: {
      true: {
        body: { "& tr": { cursor: "pointer", _hover: rowHover } },
      },
    },
    striped: {
      true: {
        row: {
          "&:nth-of-type(odd) td": { background: "var(--color-surface-2)" },
        },
      },
    },
    stickyHeader: {
      true: {
        header: {
          "& :where(tr)": {
            position: "sticky",
            top: "var(--table-sticky-offset, 0)",
            zIndex: "var(--z-sticky)",
          },
        },
      },
    },
    showColumnBorder: {
      true: {
        columnHeader: {
          "&:not(:last-of-type)": {
            borderInlineEndWidth: "1px",
            borderInlineEndColor: "var(--color-border-2)",
          },
        },
        cell: {
          "&:not(:last-of-type)": {
            borderInlineEndWidth: "1px",
            borderInlineEndColor: "var(--color-border-2)",
          },
        },
      },
    },
  },
  defaultVariants: {
    variant: "line",
    size: "sm",
  },
});

/**
 * Banner tones, as the custom properties a variant paints with. The Chakra
 * `status` names stay (`info`, `warning`, `success`, `error`, `neutral`);
 * each maps onto its Ledger family, so a `warning` alert is warn, never the
 * accent the old orange palette now resolves to.
 */
const bannerTone = (tone: "info" | "warn" | "ok" | "err") => ({
  root: {
    "--banner-bg": `var(--color-${tone}-soft)`,
    "--banner-fg": `var(--color-${tone}-soft-fg)`,
    "--banner-border": `var(--color-${tone})`,
    "--banner-icon": `var(--color-${tone})`,
  },
});

/** The Ledger Banner: a 1px border on the soft tint, an icon, text, an action. */
const bannerSurface = {
  root: {
    background: "var(--banner-bg)",
    color: "var(--banner-fg)",
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: "var(--banner-border)",
    boxShadow: "none",
  },
  indicator: { color: "var(--banner-icon)" },
} as const;

export const alertSlotRecipe = defineSlotRecipe({
  slots: ["root", "title", "description", "indicator", "content"],
  base: {
    root: {
      borderRadius: "var(--radius-3)",
      alignItems: "flex-start",
      gap: "var(--space-2)",
      fontSize: "var(--text-13)",
      lineHeight: "var(--leading-normal)",
    },
    title: {
      fontWeight: "var(--weight-medium)",
      color: "inherit",
    },
    description: {
      color: "inherit",
    },
    indicator: {
      width: "var(--icon-md)",
      height: "var(--icon-md)",
      // One line's height, so the icon sits on the first line's centre.
      marginBlock: "calc((1lh - var(--icon-md)) / 2)",
      _icon: { width: "var(--icon-md)", height: "var(--icon-md)" },
    },
    content: {
      gap: "var(--space-1)",
      minWidth: "0",
    },
  },
  variants: {
    status: {
      info: bannerTone("info"),
      warning: bannerTone("warn"),
      success: bannerTone("ok"),
      error: bannerTone("err"),
      neutral: {
        root: {
          "--banner-bg": "var(--color-surface-2)",
          "--banner-fg": "var(--color-fg)",
          "--banner-border": "var(--color-border)",
          "--banner-icon": "var(--color-fg-3)",
        },
      },
    },
    variant: {
      subtle: bannerSurface,
      surface: bannerSurface,
      outline: bannerSurface,
      solid: {
        root: {
          background: "var(--banner-icon)",
          color: "var(--color-accent-fg)",
          borderWidth: "0",
          boxShadow: "none",
        },
        indicator: { color: "var(--color-accent-fg)" },
      },
    },
    size: {
      sm: {
        root: {
          px: "var(--space-3)",
          py: "var(--space-2)",
          textStyle: "xs",
          gap: "var(--space-2)",
        },
        indicator: {
          width: "var(--icon-sm)",
          height: "var(--icon-sm)",
          marginBlock: "calc((1lh - var(--icon-sm)) / 2)",
          textStyle: "xs",
          _icon: { width: "var(--icon-sm)", height: "var(--icon-sm)" },
        },
      },
      md: {
        root: {
          px: "var(--space-3)",
          py: "var(--space-2)",
          textStyle: "sm",
          gap: "var(--space-2)",
        },
        indicator: { textStyle: "sm" },
      },
      lg: {
        root: {
          px: "var(--space-4)",
          py: "var(--space-3)",
          textStyle: "sm",
          gap: "var(--space-2)",
        },
        indicator: { textStyle: "sm" },
      },
    },
  },
});

/**
 * EmptyState: one 20px icon in fg-3, a sentence-case title, one sentence and
 * the primary action, centred with 40px above. Never a full-height
 * illustration.
 */
export const emptyStateSlotRecipe = defineSlotRecipe({
  slots: ["root", "content", "indicator", "title", "description"],
  base: {
    root: {
      width: "full",
    },
    content: {
      gap: "var(--space-2)",
      textAlign: "center",
    },
    indicator: {
      color: "var(--color-fg-3)",
      fontSize: "var(--icon-lg)",
      marginBottom: "var(--space-1)",
      _icon: { width: "var(--icon-lg)", height: "var(--icon-lg)" },
    },
    title: {
      fontSize: "var(--text-14)",
      fontWeight: "var(--weight-semibold)",
      lineHeight: "var(--leading-tight)",
      color: "var(--color-fg)",
    },
    description: {
      fontSize: "var(--text-13)",
      color: "var(--color-fg-2)",
      maxWidth: "var(--layout-prose-max)",
    },
  },
  variants: {
    size: {
      sm: {
        root: {
          px: "var(--space-4)",
          pt: "var(--space-6)",
          pb: "var(--space-4)",
        },
        content: { gap: "var(--space-2)" },
        indicator: { textStyle: "none", fontSize: "var(--icon-lg)" },
        title: { textStyle: "none", fontSize: "var(--text-14)" },
      },
      md: {
        root: {
          px: "var(--space-4)",
          pt: "var(--space-8)",
          pb: "var(--space-6)",
        },
        content: { gap: "var(--space-2)" },
        indicator: { textStyle: "none", fontSize: "var(--icon-lg)" },
        title: { textStyle: "none", fontSize: "var(--text-14)" },
      },
      lg: {
        root: {
          px: "var(--space-4)",
          pt: "var(--space-8)",
          pb: "var(--space-7)",
        },
        content: { gap: "var(--space-3)" },
        indicator: { textStyle: "none", fontSize: "var(--icon-lg)" },
        title: { textStyle: "none", fontSize: "var(--text-16)" },
      },
    },
  },
});

/**
 * Card: surface, 1px border, radius 4, 16px padding, an optional `--shadow-1`
 * (the `elevated` variant), and no transition.
 */
export const cardSlotRecipe = defineSlotRecipe({
  slots: ["root", "header", "body", "footer", "title", "description"],
  base: {
    root: {
      background: "var(--color-surface)",
      color: "var(--color-fg)",
      borderRadius: "var(--radius-3)",
      transition: "none",
      // Clip square child paints (row hover, header bands, code blocks) to
      // the rounded border. Floating content renders in portals.
      overflow: "hidden",
    },
    title: {
      fontSize: "var(--text-14)",
      fontWeight: "var(--weight-semibold)",
    },
    description: {
      fontSize: "var(--text-13)",
      color: "var(--color-fg-2)",
    },
    header: { gap: "var(--space-1)" },
    footer: { gap: "var(--space-2)" },
  },
  variants: {
    variant: {
      outline: {
        root: {
          borderWidth: "1px",
          borderStyle: "solid",
          borderColor: "var(--color-border)",
          boxShadow: "none",
        },
      },
      elevated: {
        root: {
          background: "var(--color-surface)",
          borderWidth: "1px",
          borderStyle: "solid",
          borderColor: "var(--color-border)",
          boxShadow: "var(--shadow-1)",
          _hover: { boxShadow: "var(--shadow-1)" },
        },
      },
      subtle: {
        root: {
          background: "var(--color-surface-2)",
          borderWidth: "1px",
          borderStyle: "solid",
          borderColor: "var(--color-border-2)",
        },
      },
    },
    size: {
      sm: {
        root: { "--card-padding": "var(--space-3)" },
        title: { fontSize: "var(--text-14)" },
      },
      md: {
        root: { "--card-padding": "var(--space-4)" },
        title: { fontSize: "var(--text-14)" },
      },
      lg: {
        root: { "--card-padding": "var(--space-6)" },
        title: { fontSize: "var(--text-16)" },
      },
    },
  },
  defaultVariants: {
    variant: "outline",
    size: "md",
  },
});

/** Inline code: mono on surface-2, radius 2. */
export const codeRecipe = defineRecipe({
  base: {
    fontFamily: "var(--font-mono)",
    fontSize: "0.93em",
    borderRadius: "var(--radius-1)",
  },
});

/**
 * Chakra's code block, for the few places that use it directly: surface-2,
 * mono `--text-12`, a hairline under the header, no dark colour scheme and no
 * gradient over collapsed content. New code uses `ui/code-block.tsx`.
 */
export const codeBlockSlotRecipe = defineSlotRecipe({
  slots: [
    "root",
    "content",
    "title",
    "header",
    "footer",
    "control",
    "overlay",
    "code",
    "codeText",
    "copyTrigger",
    "copyIndicator",
    "collapseTrigger",
    "collapseIndicator",
    "collapseText",
  ],
  base: {
    root: {
      background: "var(--color-surface-2)",
      color: "var(--color-fg)",
      borderColor: "var(--color-border)",
      "--code-block-radius": "var(--radius-3)",
      "--code-block-bg": "var(--color-surface-2)",
      "--code-block-fg": "var(--color-fg)",
      "--code-block-highlight-bg": "var(--color-accent-soft)",
      "--code-block-highlight-border": "var(--color-accent)",
      "--code-block-highlight-added-bg": "var(--color-ok-soft)",
      "--code-block-highlight-added-border": "var(--color-ok)",
      "--code-block-highlight-removed-bg": "var(--color-err-soft)",
      "--code-block-highlight-removed-border": "var(--color-err)",
    },
    header: {
      borderBottomWidth: "1px",
      borderBottomStyle: "solid",
      borderBottomColor: "var(--color-border-2)",
      marginBottom: "0",
    },
    title: {
      color: "var(--color-fg-3)",
      fontFamily: "var(--font-mono)",
      fontSize: "var(--text-11)",
    },
    content: {
      colorScheme: "inherit",
      "& ::selection": { background: "var(--color-accent-soft)" },
    },
    overlay: {
      backgroundImage: "none",
      background: "var(--color-surface-2)",
      color: "var(--color-fg-2)",
    },
    code: {
      fontFamily: "var(--font-mono)",
      fontSize: "var(--text-12)",
      lineHeight: "var(--leading-normal)",
    },
  },
});

/**
 * Stat: label `--text-12` fg-3, value `--text-22` (28 at `lg`) tabular,
 * help text `--text-12` fg-2, the change mark in ok or err.
 */
export const statSlotRecipe = defineSlotRecipe({
  slots: ["root", "label", "helpText", "valueText", "valueUnit", "indicator"],
  base: {
    root: { gap: "var(--space-1)" },
    label: {
      color: "var(--color-fg-3)",
      textStyle: "xs",
      fontWeight: "var(--weight-medium)",
    },
    helpText: {
      color: "var(--color-fg-2)",
      textStyle: "xs",
    },
    valueText: {
      fontWeight: "var(--weight-semibold)",
      letterSpacing: "normal",
      fontFeatureSettings: "normal",
      fontVariantNumeric: "tabular-nums",
      lineHeight: "var(--leading-tight)",
    },
    valueUnit: {
      color: "var(--color-fg-3)",
      textStyle: "xs",
    },
    indicator: {
      "&[data-type=up]": { color: "var(--color-ok)" },
      "&[data-type=down]": { color: "var(--color-err)" },
    },
  },
  variants: {
    size: {
      // textStyles xl, 2xl and 3xl are --text-18, --text-22 and --text-28
      // (ledgerTheme.ts); the same key as Chakra's sizes, so these replace them.
      sm: { valueText: { textStyle: "xl" } },
      md: { valueText: { textStyle: "2xl" } },
      lg: { valueText: { textStyle: "3xl" } },
    },
  },
});

/** The focus ring for a row that is itself a link (`DataTable` row links). */
export const rowLinkFocus = {
  _focusVisible: { ...focusRing, borderRadius: "0" },
} as const;
